import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ReportGeneration.css';
import { getValidAccessToken } from '../utils/tokenUtils';
import { Button } from '@mui/material';

const apiUrl = process.env.REACT_APP_API_URL;
const ReportGeneration = () => {
  const [reportTypes, setReportTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reportData, setReportData] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    report_name: '',
    report_type: 'ticket_summary',
    start_date: '',
    end_date: '',
    file_format: 'json'
  });

  // Fetch report types on mount
  useEffect(() => {
    fetchReportTypes();
  }, []);

  const fetchReportTypes = async () => {
    try {
      const token = await getValidAccessToken();
      const response = await axios.get(
        `${apiUrl}/reports/types/`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setReportTypes(response.data.report_types);
    } catch (err) {
      console.error('Error fetching report types:', err);
      setError('Failed to load report types');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    
    if (!formData.report_name || !formData.start_date || !formData.end_date) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      setError('Start date must be before end date');
      return;
    }

    setGeneratingReport(true);
    setError('');
    setSuccess('');
    setReportData(null);

    try {
      const token = await getValidAccessToken();
      const response = await axios.post(
        `${apiUrl}/reports/generate/`,
        {
          report_name: formData.report_name,
          report_type: formData.report_type,
          start_date: formData.start_date,
          end_date: formData.end_date,
          file_format: formData.file_format
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setReportData(response.data);
      setSuccess(`${formData.report_type} report generated successfully! Ready to download.`);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.response?.data?.error || 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportData) return;

    setLoading(true);
    setError('');

    try {
      const token = await getValidAccessToken();
      const response = await axios.post(
        `${apiUrl}/reports/download/`,
        {
          report_type: formData.report_type,
          start_date: formData.start_date,
          end_date: formData.end_date,
          file_format: formData.file_format,
          filename: formData.report_name
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = formData.file_format === 'csv' ? 'csv' : 'json';
      link.setAttribute('download', `${formData.report_name}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Report downloaded successfully!');
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Failed to download report');
    } finally {
      setLoading(false);
    }
  };

  const handleClearForm = () => {
    setFormData({
      report_name: '',
      report_type: 'ticket_summary',
      start_date: '',
      end_date: '',
      file_format: 'json'
    });
    setReportData(null);
    setError('');
    setSuccess('');
  };

  return (
    <div className="report-generation-container">
      <div className="report-header">
        <h2>Report Generation</h2>
        <p>Generate reports from Tickets table data</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="close-btn">&times;</button>
        </div>
      )}

      <div className="report-content">
        <form onSubmit={handleGenerateReport} className="report-form">
          <div className="form-group">
            <label htmlFor="report_name">Report Name *</label>
            <input
              type="text"
              id="report_name"
              name="report_name"
              value={formData.report_name}
              onChange={handleInputChange}
              placeholder="e.g., Monthly Report Jan 2024"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="report_type">Report Type *</label>
              <select
                id="report_type"
                name="report_type"
                value={formData.report_type}
                onChange={handleInputChange}
                required
              >
                {reportTypes.map(type => (
                  <option key={type.type} value={type.type}>
                    {type.name}
                  </option>
                ))}
              </select>
              <small>
                {reportTypes.find(t => t.type === formData.report_type)?.description}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="file_format">Export Format</label>
              <select
                id="file_format"
                name="file_format"
                value={formData.file_format}
                onChange={handleInputChange}
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start_date">Start Date *</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date">End Date *</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-actions" style={{marginTop:'2rem', display:'flex', gap:'10px'}}>
            <Button
              type="submit"
              className="btn btn-primary"
              variant="contained"
              disabled={generatingReport}
            >
              {generatingReport ? 'Generating...' : 'Generate Report'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              className="btn btn-secondary"
              onClick={handleClearForm}
            >
              Clear
            </Button>
          </div>
        </form>

        {reportData && (
          <div className="report-result">
            <div className="result-header">
              <h3>Report Generated Successfully</h3>
              <p className="result-meta">
                Generated at: {new Date(reportData.generated_at).toLocaleString()}
              </p>
            </div>

            <div className="report-summary">
              <div className="summary-item">
                <span className="label">Report Type:</span>
                <span className="value">
                  {reportTypes.find(t => t.type === reportData.report_type)?.name}
                </span>
              </div>
              <div className="summary-item">
                <span className="label">Format:</span>
                <span className="value">{reportData.file_format.toUpperCase()}</span>
              </div>
              <div className="summary-item">
                <span className="label">Records:</span>
                <span className="value">{reportData.data?.records?.length || 0}</span>
              </div>
            </div>

            <div className="report-preview">
              <h4>Data Preview</h4>
              <pre>{JSON.stringify(reportData.data?.summary, null, 2)}</pre>
            </div>

            <div className="result-actions">
              <Button
                onClick={handleDownloadReport}
                className="btn btn-success"
                disabled={loading}
                 variant="contained"
              >
                {loading ? 'Downloading...' : '⬇ Download Report'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportGeneration;
